import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Wallet,
  Package,
  Target,
  AlertTriangle,
  Zap,
  ArrowRight,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { KPICard } from '../components/shared/KPICard';
import { StatusBadge } from '../components/shared/StatusBadge';
import { Button } from '../components/ui/button';
import { dashboardAPI, actualsAPI } from '../lib/api';
import { formatCurrency, CHART_COLORS, MONTHS, CHANNEL_NAMES } from '../lib/utils';

export default function DashboardPage() {
  const [kpis, setKpis] = useState(null);
  const [actuals, setActuals] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [kpiRes, actualsRes] = await Promise.all([
          dashboardAPI.getKPIs(),
          actualsAPI.get(2024),
        ]);
        setKpis(kpiRes.data);
        setActuals(actualsRes.data);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="kpi-card animate-pulse">
              <div className="h-4 bg-slate-200 rounded w-24 mb-3" />
              <div className="h-8 bg-slate-200 rounded w-32" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Prepare chart data
  const revenueData = actuals?.monthly_data?.map((m) => ({
    month: MONTHS[m.month - 1],
    revenue: m.revenue / 100000, // Convert to Lakhs
    ebitda: m.ebitda / 100000,
  })) || [];

  const channelData = actuals?.monthly_data?.[11]?.channel_data?.map((c) => ({
    name: CHANNEL_NAMES[c.channel] || c.channel,
    value: c.revenue,
    roas: c.roas,
  })) || [];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-stagger">
        <KPICard
          title="Revenue vs AOP"
          value={kpis?.revenue_vs_aop?.actual}
          change={kpis?.revenue_vs_aop?.variance_pct}
          changeLabel="vs plan"
          format="currency"
          icon={DollarSign}
          trend={kpis?.revenue_vs_aop?.variance_pct >= 0 ? 'up' : 'down'}
          status={kpis?.revenue_vs_aop?.variance_pct >= -5 ? 'good' : 'bad'}
        />
        <KPICard
          title="EBITDA vs AOP"
          value={kpis?.ebitda_vs_aop?.actual}
          change={kpis?.ebitda_vs_aop?.variance_pct}
          changeLabel="vs plan"
          format="currency"
          icon={Target}
          trend={kpis?.ebitda_vs_aop?.variance_pct >= 0 ? 'up' : 'down'}
          status={kpis?.ebitda_vs_aop?.variance_pct >= -5 ? 'good' : 'bad'}
        />
        <KPICard
          title="Cash Runway"
          value={kpis?.runway_months}
          format="months"
          icon={Wallet}
          status={kpis?.runway_months >= 6 ? 'good' : kpis?.runway_months >= 3 ? undefined : 'bad'}
        />
        <KPICard
          title="Marketing ROAS"
          value={kpis?.marketing_efficiency}
          format="multiple"
          icon={TrendingUp}
          status={kpis?.marketing_efficiency >= 3 ? 'good' : 'bad'}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Trend */}
        <div className="lg:col-span-2 chart-container">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-heading font-semibold text-lg text-slate-900">Revenue & EBITDA Trend</h3>
              <p className="text-sm text-slate-500">Monthly performance in Lakhs (₹L)</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={revenueData}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#DC5829" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#DC5829" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorEbitda" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748B' }} />
              <YAxis tick={{ fontSize: 12, fill: '#64748B' }} tickFormatter={(v) => `₹${v}L`} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                }}
                formatter={(value) => [`₹${value.toFixed(1)}L`, '']}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#DC5829"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorRevenue)"
                name="Revenue"
              />
              <Area
                type="monotone"
                dataKey="ebitda"
                stroke="#10B981"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorEbitda)"
                name="EBITDA"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Channel Mix */}
        <div className="chart-container">
          <div className="mb-6">
            <h3 className="font-heading font-semibold text-lg text-slate-900">Channel Mix</h3>
            <p className="text-sm text-slate-500">Revenue distribution</p>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={channelData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
              >
                {channelData.map((entry, index) => (
                  <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value, { compact: true })} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2">
            {channelData.slice(0, 4).map((channel, index) => (
              <div key={channel.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: CHART_COLORS[index] }}
                  />
                  <span className="text-slate-600">{channel.name}</span>
                </div>
                <span className="font-mono text-slate-900">{channel.roas}x</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Risks and Growth Levers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Risks */}
        <div className="chart-container">
          <div className="flex items-center gap-2 mb-6">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h3 className="font-heading font-semibold text-lg text-slate-900">Top Risks</h3>
          </div>
          <div className="space-y-3">
            {kpis?.top_risks?.map((risk, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className="flex-1">
                  <p className="font-medium text-slate-900 text-sm">{risk.risk}</p>
                  <p className="text-xs text-slate-500">{risk.impact}</p>
                </div>
                <StatusBadge status={risk.severity} label={risk.severity} size="sm" />
              </div>
            ))}
          </div>
        </div>

        {/* Growth Levers */}
        <div className="chart-container">
          <div className="flex items-center gap-2 mb-6">
            <Zap className="w-5 h-5 text-primary-600" />
            <h3 className="font-heading font-semibold text-lg text-slate-900">Growth Levers</h3>
          </div>
          <div className="space-y-3">
            {kpis?.top_growth_levers?.map((lever, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors cursor-pointer group"
              >
                <div className="flex-1">
                  <p className="font-medium text-slate-900 text-sm">{lever.lever}</p>
                  <p className="text-xs text-primary-700">{lever.potential}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scenario Summary */}
      <div className="chart-container">
        <div className="mb-6">
          <h3 className="font-heading font-semibold text-lg text-slate-900">Scenario Planning</h3>
          <p className="text-sm text-slate-500">Revenue and EBITDA projections by scenario</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {['base', 'aggressive', 'conservative'].map((scenario) => {
            const data = kpis?.scenario_summary?.[scenario];
            if (!data) return null;

            const isBase = scenario === 'base';
            return (
              <div
                key={scenario}
                className={`p-4 rounded-lg border ${
                  isBase ? 'border-primary-200 bg-primary-50' : 'border-slate-200 bg-white'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-slate-900 capitalize">{scenario}</span>
                  <span className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded">
                    {data.probability}% prob
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Revenue</span>
                    <span className="font-mono text-slate-900">
                      {formatCurrency(data.revenue, { compact: true })}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">EBITDA</span>
                    <span className="font-mono text-slate-900">
                      {formatCurrency(data.ebitda, { compact: true })}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
